import { useState, useRef, useEffect } from 'react'
import { MoreHorizontal } from 'lucide-react'
import CopyBugReportsButton from './CopyBugReportsButton'
import DownloadBugReportsButton from './DownloadBugReportsButton'
import ConnectIssueTrackerButton from './ConnectIssueTrackerButton'
import './BugListActions.css'

interface BugListActionsProps {
  projectId: string
  statusFilter: string
  severitySort: 'asc' | 'desc' | null
  severityFilter: string
  kindFilter: string
  environmentFilter: string
  disabled: boolean
  isMobile: boolean
  // False until the project has an issue tracker connected — while false we surface the connect CTA
  // beside the copy/download actions.
  issueTrackerConnected: boolean
}

// The toolbar's action buttons (Copy Bug Reports + Missing Bug). Rendered inline on desktop; on
// mobile they collapse behind a "..." button into a small dropdown so the toolbar stays compact.
export default function BugListActions({
  projectId,
  statusFilter,
  severitySort,
  severityFilter,
  kindFilter,
  environmentFilter,
  disabled,
  isMobile,
  issueTrackerConnected,
}: BugListActionsProps) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKey)
    }
  }, [open])

  const buttons = (
    <>
      <CopyBugReportsButton
        projectId={projectId}
        status={statusFilter}
        severitySort={severitySort}
        severity={severityFilter}
        kind={kindFilter}
        environment={environmentFilter}
        disabled={disabled}
      />
      <DownloadBugReportsButton
        projectId={projectId}
        status={statusFilter}
        severitySort={severitySort}
        severity={severityFilter}
        kind={kindFilter}
        environment={environmentFilter}
        disabled={disabled}
      />
      {!issueTrackerConnected && <ConnectIssueTrackerButton projectId={projectId} />}
    </>
  )

  if (!isMobile) {
    return <div className="bug-list-actions">{buttons}</div>
  }

  return (
    <div className="bug-list-actions bug-list-actions--mobile" ref={ref}>
      <button
        type="button"
        className="bug-list-actions__trigger"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Bug actions"
        title="Actions"
        data-testid="bug-list-actions-trigger"
        data-analytics-event="bug-list-actions-toggle"
      >
        <MoreHorizontal size={16} aria-hidden="true" />
      </button>
      {open && (
        <div
          className="bug-list-actions__menu"
          role="menu"
          aria-label="Bug actions"
          data-testid="bug-list-actions-menu"
          onClick={() => setOpen(false)}
        >
          {buttons}
        </div>
      )}
    </div>
  )
}
